import { useState, useEffect, useCallback } from 'react';

export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Parse JWT token and extract user info
  const parseToken = useCallback((token) => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return {
        id: payload.id,
        username: payload.username,
        role: payload.role,
        exp: payload.exp
      };
    } catch (error) {
      console.error('Error parsing token:', error);
      return null;
    }
  }, []);

  // Check if token is expired
  const isTokenExpired = useCallback((exp) => {
    return Date.now() >= exp * 1000;
  }, []);

  // Initialize auth state
  useEffect(() => {
    const token = localStorage.getItem('token');
    
    if (!token) {
      setUser(null);
      setIsLoading(false);
      return;
    }

    const userData = parseToken(token);
    
    if (!userData) {
      localStorage.removeItem('token');
      setUser(null);
      setIsLoading(false);
      return;
    }

    if (isTokenExpired(userData.exp)) {
      localStorage.removeItem('token');
      setUser(null);
      setIsLoading(false);
      return;
    }

    setUser(userData);
    setIsLoading(false);
  }, [parseToken, isTokenExpired]);

  // Login function
  const login = useCallback((token) => {
    localStorage.setItem('token', token);
    const userData = parseToken(token);
    setUser(userData);
    setError(null);
  }, [parseToken]);

  // Logout function
  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setUser(null);
    setError(null);
  }, []);

  // Check if user has specific role
  const hasRole = useCallback((requiredRole) => {
    if (!user) return false;
    return user.role === requiredRole || user.role === 'admin';
  }, [user]);

  // Check if user can access specific resource
  const canAccess = useCallback((resource, action = 'read') => {
    if (!user) return false;
    
    // Admin can do everything
    if (user.role === 'admin') return true;
    
    // Contract users can only manage their own contracts and events
    if (user.role === 'contract_user') {
      if (resource === 'contracts' && ['create', 'read', 'update', 'delete'].includes(action)) {
        return true;
      }
      if (resource === 'events' && ['create', 'read', 'update', 'delete'].includes(action)) {
        return true;
      }
      if (resource === 'singers') {
        return false; // Contract users cannot access singers
      }
    }
    
    return false;
  }, [user]);

  // Get user's permissions
  const getPermissions = useCallback(() => {
    if (!user) return {};
    
    if (user.role === 'admin') {
      return {
        canManageUsers: true,
        canManageSingers: true,
        canManageAllContracts: true,
        canManageAllEvents: true,
        canAccessAdminPanel: true
      };
    }
    
    if (user.role === 'contract_user') {
      return {
        canManageUsers: false,
        canManageSingers: false,
        canManageAllContracts: false,
        canManageAllEvents: false,
        canAccessAdminPanel: false,
        canCreateContracts: true,
        canManageOwnContracts: true,
        canCreateEvents: true,
        canManageOwnEvents: true
      };
    }
    
    return {};
  }, [user]);

  return {
    user,
    isLoading,
    error,
    login,
    logout,
    hasRole,
    canAccess,
    getPermissions,
    isAuthenticated: !!user
  };
};

